/* oxlint-disable no-console */
import { getAppId } from '@dynatrace-sdk/app-environment';

import type { BehaviorTrackingProps } from '../types/behavior-tracking-props.js';
import {
  Logger,
  GlobalDtRuntimeWithLogger,
  LoggerFactory,
  LoggerFactoryV1,
} from './sdk-logger.types.js';

//globalThis is not supported in Chrome 69 and Safari 12
/** @internal */
export const getGlobalWithDtRuntime = <T extends typeof globalThis>(): T =>
  (typeof globalThis !== 'undefined' ? globalThis : window) as unknown as T;

/**
 * Checks if the logger v2 create fn is available on platform
 * @internal
 */
export const supportsLoggerV2 = () =>
  typeof getGlobalWithDtRuntime<GlobalDtRuntimeWithLogger>().dtRuntime?.logger
    ?.createLoggerV2 === 'function';

/**
 * Checks if the logger v1 create fn is available on platform
 * @internal
 */
export const supportsLoggerV1 = () =>
  typeof getGlobalWithDtRuntime<GlobalDtRuntimeWithLogger>().dtRuntime?.logger
    ?.createLogger === 'function';

/**
 * The logger factory is used to create logger instances for structured message recording.
 * It checks if the logger v2 or v1 is available and creates the appropriate logger factory.
 * If neither is available, it falls back to a simple console wrapper.
 */
const LOGGER_FACTORY = (() => {
  let instance: LoggerFactory | LoggerFactoryV1 | null;

  const createLogger = () => {
    const dtRuntimeLogger =
      getGlobalWithDtRuntime<GlobalDtRuntimeWithLogger>().dtRuntime?.logger;

    if (dtRuntimeLogger === undefined) {
      return null;
    }

    if (supportsLoggerV2()) {
      return dtRuntimeLogger.createLoggerFactoryV2({
        context: {
          id: '@dynatrace/strato-design-system',
          // @ts-expect-error Global variable injected at package build time
          version: STRATO_PKG_VERSION,
        },
      });
    }
    // seperate check for outside of Dynatrace enviroment
    else if (supportsLoggerV1()) {
      return dtRuntimeLogger.createLoggerFactory({
        context: {
          id: '@dynatrace/strato-design-system',
          // @ts-expect-error Global variable injected at package build time
          version: STRATO_PKG_VERSION,
        },
      });
    }
    return null;
  };

  return {
    getInstace: () => {
      if (!instance) {
        instance = createLogger();
      }
      return instance;
    },
  };
})();

/**
 * Creates a logger instance for structured message recording.
 *
 * @param name - Identifier for the logger instance.
 * @returns An instance of the Logger utility.
 * @internal
 */
const createSdkLogger = (name: string): Logger | undefined => {
  if (LOGGER_FACTORY) {
    return LOGGER_FACTORY.getInstace()?.createLogger(name);
  } else {
    const wrapConsole =
      (
        logFunc:
          | typeof console.log
          | typeof console.warn
          | typeof console.error,
      ) =>
      (...args: Parameters<Logger['info' | 'warn' | 'error']>) =>
        logFunc(`[${name}]: ${args[0]}`, args[1]);

    return Object.freeze({
      info: wrapConsole(console.log),
      warn: wrapConsole(console.warn),
      error: wrapConsole(console.error),
    } as Logger);
  }
};

const isBuiltInAppid = (appId: string) => appId.startsWith('dynatrace.');

const isComponentDebugLoggingEnabled = (componentName: string) =>
  window.localStorage.getItem(`_strato_${componentName}_logging_enabled_`) ===
  'true';

/** @internal */
export type StratoLogger = {
  error: (message: string, error?: unknown, details?: unknown) => void;
  warn: (message: string, error?: unknown, details?: unknown) => void;
  info: (message: string, error?: unknown, details?: unknown) => void;
};

type StratoLoggerContextProps = {
  name?: string;
  id?: string;
  [key: string]: unknown;
} & BehaviorTrackingProps;

/**
 * Creates a Strato-logger instance for structured message recording.
 *
 * @param componentName - Name of the Component or hook that is logged.
 * @returns An instance of the Logger utility.
 * @internal
 */
export function createStratoLogger(
  this: StratoLoggerContextProps | void,
  componentName: string,
): StratoLogger {
  const isBuiltInApp = isBuiltInAppid(getAppId());
  const logger = createSdkLogger('Strato: ' + componentName);

  const log = (
    level: 'info' | 'warn' | 'error',
    message?: string,
    error?: unknown,
    details: unknown = {},
  ) => {
    if (logger === undefined) {
      return;
    }

    const logMessage = message?.trim() || 'An error occurred'; // ?? not used as also empty should get generic message

    const shouldLog =
      isBuiltInApp || isComponentDebugLoggingEnabled(componentName);

    if (!shouldLog) {
      return;
    }

    let context = 'undefined';
    if (this) {
      try {
        context = JSON.stringify(this);
      } catch (e) {
        console.error(e);
        context = 'could not be stringified';
      }
    }

    if (supportsLoggerV2()) {
      logger[level](`${logMessage}; context: ${context}`, {
        error,
        details,
      });
    } else {
      try {
        logger[level](
          `${logMessage}; details: ${JSON.stringify(
            details,
          )}; context: ${context};`,
          error,
        );
      } catch (e) {
        console.error(e);
        logger[level](
          `${logMessage}; details: details could not be stringified; context: ${context};`,
          error,
        );
      }
    }
  };

  const error = (message: string, error?: unknown, details?: unknown) => {
    log('error', message, error, details);
  };
  const warn = (message: string, error?: unknown, details?: unknown) => {
    log('warn', message, error, details);
  };
  const info = (message: string, error?: unknown, details?: unknown) => {
    log('info', message, error, details);
  };

  return {
    error: error,
    warn: warn,
    info: info,
  };
}

/** A cache for Strato-logger instances, keyed by component name.    */
const stratoLoggers: Record<string, StratoLogger> = {};

/**
 * Returns a Strato-logger instance for structured message recording.
 * If the logger for the given componentName already exists, it returns the existing instance.
 * Otherwise, it creates a new logger instance.
 *
 * @param componentName - Name of the Component or hook that is logged.
 * @returns An instance of the Logger utility.
 *
 * @internal
 */
export const getStratoLogger = (componentName: string): StratoLogger => {
  if (!stratoLoggers[componentName]) {
    stratoLoggers[componentName] = createStratoLogger(componentName);
  }
  return stratoLoggers[componentName];
};
